import { useCallback, useState } from 'react';
import type React from 'react';

/**
 * 大包进度 2 级表 MAWB 行展开 / 收起 + 三级 rows 缓存的通用 hook。
 *
 * 包裹差异 tab 与大包差异 tab 都需要：
 * - 维护「每客户已展开的 MAWB 列表」用于 expand 状态控制
 * - 缓存 `${customerId}-${mawb}` → rows 与 loading 标记，避免重复请求
 * - 收起时清掉对应缓存（与原 toggle*Mawb* 函数行为一致）
 *
 * 两个 tab 唯一不同点是 fetcher（GraphQL 查询）与 row 类型，故 hook 用 `T` 泛化。
 */
export function useMawbExpandCache<T>(fetcher: (customerId: number, mawb: string) => Promise<T[]>) {
  const [rowsByKey, setRowsByKey] = useState<Record<string, T[]>>({});
  const [loadingByKey, setLoadingByKey] = useState<Record<string, boolean>>({});
  const [expandedKeysByCustomerId, setExpandedKeysByCustomerId] = useState<Record<number, React.Key[]>>({});

  const keyOf = (customerId: number, mawb: string) => `${customerId}-${mawb}`;

  const toggle = useCallback(
    async (customerId: number, mawb: string, nextOpen: boolean) => {
      const cacheKey = keyOf(customerId, mawb);
      if (!nextOpen) {
        setExpandedKeysByCustomerId((prev) => ({
          ...prev,
          [customerId]: (prev[customerId] ?? []).filter((k) => k !== mawb),
        }));
        setRowsByKey((prev) => {
          if (!(cacheKey in prev)) return prev;
          const { [cacheKey]: _drop, ...rest } = prev;
          return rest;
        });
        setLoadingByKey((prev) => {
          if (!(cacheKey in prev)) return prev;
          const { [cacheKey]: _drop, ...rest } = prev;
          return rest;
        });
        return;
      }
      setExpandedKeysByCustomerId((prev) => ({
        ...prev,
        [customerId]: Array.from(new Set([...(prev[customerId] ?? []), mawb])),
      }));
      // 已有缓存命中跳过重复请求，保持原 toggle*Mawb* 行为。
      if (rowsByKey[cacheKey]) return;
      setLoadingByKey((prev) => ({ ...prev, [cacheKey]: true }));
      try {
        const items = await fetcher(customerId, mawb);
        setRowsByKey((prev) => ({ ...prev, [cacheKey]: items }));
      } finally {
        setLoadingByKey((prev) => ({ ...prev, [cacheKey]: false }));
      }
    },
    [fetcher, rowsByKey],
  );

  /** 客户行收起 / 切换 tab 时连带清掉该客户名下的所有 MAWB 缓存与展开态。 */
  const clearForCustomer = useCallback((customerId: number) => {
    const prefix = `${customerId}-`;
    const dropPrefix = <V,>(obj: Record<string, V>): Record<string, V> => {
      const next: Record<string, V> = {};
      let changed = false;
      for (const [k, v] of Object.entries(obj)) {
        if (k.startsWith(prefix)) {
          changed = true;
        } else {
          next[k] = v;
        }
      }
      return changed ? next : obj;
    };
    setRowsByKey((prev) => dropPrefix(prev));
    setLoadingByKey((prev) => dropPrefix(prev));
    setExpandedKeysByCustomerId((prev) => {
      if (!(customerId in prev)) return prev;
      const { [customerId]: _drop, ...rest } = prev;
      return rest;
    });
  }, []);

  /** 切换子 tab / 时间筛选变化时一次性清空。 */
  const clearAll = useCallback(() => {
    setRowsByKey({});
    setLoadingByKey({});
    setExpandedKeysByCustomerId({});
  }, []);

  return {
    rowsByKey,
    loadingByKey,
    expandedKeysByCustomerId,
    toggle,
    clearForCustomer,
    clearAll,
    rowsFor: (customerId: number, mawb: string): T[] => rowsByKey[keyOf(customerId, mawb)] ?? [],
    isLoading: (customerId: number, mawb: string): boolean => Boolean(loadingByKey[keyOf(customerId, mawb)]),
    isExpanded: (customerId: number, mawb: string): boolean =>
      (expandedKeysByCustomerId[customerId] ?? []).includes(mawb),
  };
}
